import { UseCaseComment } from "lib/models/user-insights-models";
import { CSRFService } from "lib/services/csrf-service";
import { UserInformation } from "lib/services/user-information-service";
import { UserInsightsService } from "lib/services/user-insights-service";
import { useEffect, useMemo, useState } from "react";

interface UseUseCaseCommentsResult {
  comments: UseCaseComment[];
  isLoading: boolean;
  error: string | null;
  postComment: (text: string, parentCommentId?: string) => Promise<void>;
}

export function useUseCaseComments(useCaseId: string): UseUseCaseCommentsResult {
  const [comments, setComments] = useState<UseCaseComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const service = useMemo(() => new UserInsightsService(), []);
  const csrfService = useMemo(() => new CSRFService(), []);

  useEffect(() => {
    if (!useCaseId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    service
      .listComments(useCaseId)
      .then((res) => setComments(res.comments))
      .catch(() => setComments([]))
      .finally(() => setIsLoading(false));
  }, [useCaseId]);

  const postComment = async (text: string, parentCommentId?: string) => {
    setError(null);
    const csrfToken = await csrfService.getToken();
    const userInfo = UserInformation.current.getCachedAmazonianInformation();
    const authorFullName = `${userInfo.basicInfo.firstName} ${userInfo.basicInfo.lastName}`;
    const newComment = await service.postComment(useCaseId, { text, authorFullName, parentCommentId }, csrfToken);

    if (parentCommentId) {
      setComments((prev) =>
        prev.map((c) => (c.commentId === parentCommentId ? { ...c, replies: [...(c.replies || []), newComment] } : c))
      );
    } else {
      setComments((prev) => [...prev, newComment]);
    }
  };

  return { comments, isLoading, error, postComment };
}
